import { useCallback, useRef } from 'react';

import { fg } from '@atlaskit/platform-feature-flags';

import {
	BFF_REQUEST_STATUS_KEY,
	MAX_ATTEMPTS_DEFAULT,
	MAX_ATTEMPTS_NEW_DEFAULT,
	RequestStatus,
	RETRY_INTERVAL_DEFAULT,
} from '../../constants';
import { fetchJSON } from '../utils/request';

import { type CacheItem, useCache } from './useCache';
import { type ProductLink, useProductLinks } from './useProductLinks';
import { useStatus } from './useStatus';
import { useURLBuilder } from './useURLBuilder';

type useBFFProps = {
	cloudId?: string;
	forceStatic?: boolean;
	orgId?: string;
	product?: string;
	request?: {
		attemptInterval?: number;
		enableExponentialDelay?: boolean;
		maxAttempts?: number;
		shouldRetryOnException?: boolean;
	};
	userId?: string;
};

async function wait(ms: number) {
	return new Promise((resolve) => {
		setTimeout(resolve, ms);
	});
}

export function useBFFDataProvider({
	request,
	cloudId,
	orgId,
	product,
	userId,
	forceStatic,
}: useBFFProps): {
	addItemsToCache: <T extends CacheItem>(items: T[], compareKey?: keyof CacheItem) => void;
	buildURL: ({ baseUrl }: { baseUrl?: string }) => string;
	buildURLAndFetchData: <_T>() => Promise<void>;
	cachedItems: CacheItem[];
	cacheKey: string;
	clearCache: () => void;
	error: Error | null | undefined;
	fetchData: <_T>(
		url: string,
		data?: any,
		attemptCount?: number,
		successCheck?: (result: any) => boolean,
	) => Promise<any>;
	productLinks: Record<string, ProductLink[]>;
	status: RequestStatus | null;
	updateCacheKey: (updatedCacheKey: string) => void;
} {
	const { items: cachedItems, addItemsToCache, clearCache, cacheKey, updateCacheKey } = useCache();
	const { status, error, setStatus } = useStatus();
	const { productLinks } = useProductLinks({ cloudId, orgId, forceStatic });
	const { buildURL } = useURLBuilder({ cloudId, userId, product });

	const currentRequest = useRef<Promise<any> | null>(null);

	const fetchData = useCallback(
		async <T>(
			url: string,
			data?: any,
			attemptCount: number = 1,
			successCheck?: (result: any) => boolean,
		): Promise<any> => {
			if (url === '') {
				throw new Error('URL is empty');
			}

			if (fg('navx-3809-bff-forbidden-response-handle')) {
				// Always clear error status at the start of a new request
				setStatus(RequestStatus.LOADING, BFF_REQUEST_STATUS_KEY, undefined);
			} else {
				setStatus(RequestStatus.LOADING, BFF_REQUEST_STATUS_KEY);
			}

			try {
				const response = await fetchJSON<T>(url, {});
				switch (response.status) {
					case RequestStatus.ERROR:
						throw response.error;
					case RequestStatus.COMPLETE:
						if (fg('navx-3809-bff-forbidden-response-handle')) {
							setStatus(RequestStatus.COMPLETE, BFF_REQUEST_STATUS_KEY, undefined);
							currentRequest.current = null;

							return response.data;
						} else {
							if (!successCheck || successCheck(response.data)) {
								setStatus(RequestStatus.COMPLETE, BFF_REQUEST_STATUS_KEY);
								currentRequest.current = null;

								return response.data;
							} else {
								throw new Error('BFF success check failed');
							}
						}
				}
			} catch (error: unknown) {
				const maxAttempts =
					request?.maxAttempts ??
					(fg('navx-4044-set-default-retry-logic-to-none')
						? MAX_ATTEMPTS_NEW_DEFAULT
						: MAX_ATTEMPTS_DEFAULT);

				if (request?.shouldRetryOnException && attemptCount < maxAttempts) {
					const baseInterval = request?.attemptInterval ?? RETRY_INTERVAL_DEFAULT;

					// Exponential backoff: delay doubles with each attempt (1x, 2x, 4x)
					const delay = request?.enableExponentialDelay
						? baseInterval * Math.pow(2, attemptCount - 1)
						: baseInterval;

					await wait(delay);
					return await fetchData(url, data, attemptCount + 1, successCheck);
				} else {
					currentRequest.current = null;
					if (fg('navx-3809-bff-forbidden-response-handle')) {
						setStatus(
							RequestStatus.ERROR,
							undefined,
							error instanceof Error ? error : new Error(String(error)),
						);
						throw error;
					} else {
						const err = new Error('Max attempts reached');
						setStatus(RequestStatus.ERROR);
						throw err;
					}
				}
			}
		},
		[
			request?.attemptInterval,
			request?.maxAttempts,
			request?.shouldRetryOnException,
			request?.enableExponentialDelay,
			setStatus,
		],
	);

	/**
	 * Helper function to build a URL and fetch data.
	 */
	const buildURLAndFetchData = useCallback(async <T>(): Promise<void> => {
		// Wait for the current request to end before firing another
		if (currentRequest.current) {
			return currentRequest.current;
		}

		const builtUrl = buildURL({});

		if (builtUrl !== '') {
			currentRequest.current = fetchData<T>(
				builtUrl,
				undefined,
				undefined,
				fg('navx-3809-bff-forbidden-response-handle')
					? undefined
					: (result) => {
							return !!result?.sites?.length;
						},
			);

			const fetchedData = await currentRequest.current;

			if (fg('navx-3809-bff-forbidden-response-handle')) {
				if (!!fetchedData?.sites?.length) {
					addItemsToCache(fetchedData.sites);
				}
			} else {
				if (fetchedData.sites && fetchedData.sites.length) {
					addItemsToCache(fetchedData.sites);
				}
			}
		}
	}, [addItemsToCache, buildURL, fetchData]);

	return {
		error,
		status,
		cachedItems,
		cacheKey,
		updateCacheKey,
		addItemsToCache,
		clearCache,
		buildURL,
		fetchData,
		buildURLAndFetchData,
		productLinks,
	};
}
